import {
  Button,
  Card,
  ChakraProvider,
  Grid,
  HStack,
  IconButton,
  Stack,
  Tab,
  TabList,
  Tabs,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import axios from "axios";
import { endOfMonth, format, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { Check, Eye, Menu } from "react-feather";
import { useLocation, useNavigate } from "react-router-dom";
import { fallbackDate } from "../../config/dashboardDefaults";
import { useCurrentProfile } from "../../ContextWrapper";
import theme from "../../theme";
import { getCustomerDashboardDate } from "../../utils/getCustomerDashboardDate";
import DashboardDropdown from "./dashboardDateComponent";
import ClientDashboardLayout from "./dashboardLayout";
import DetailView from "./detailView";
import MetricView from "./metricView";
import { Box } from "@mui/material";
import TargetTypeToggle from "../dashboardComponents/targetToggle";

const PerformanceReview = () => {
  const [isMobile] = useMediaQuery("(max-width: 1280px)", { ssr: false });
  const { profile } = useCurrentProfile();
  const [activeCustomer, setActiveCustomer] = useState(null);

  useEffect(() => {
    if (!profile) return;

    const storedId = sessionStorage.getItem("selectedCustomerId");
    if (!storedId) return;

    if (profile.type !== "admin") {
      const customer =
        profile.customers.find((c) => c.id.toString() === storedId) ||
        profile.customers[0] ||
        null;
      setActiveCustomer(customer);
      return;
    }

    const fetchCustomer = async () => {
      try {
        const res = await axios.get(
          `/api/v2/customers/${storedId}/`,
          { headers: { "Content-Type": "application/json" } },
          { withCredentials: true }
        );
        setActiveCustomer(res.data);
      } catch (err) {
        console.error("Error fetching admin customer:", err);
      }
    };

    fetchCustomer();
  }, [profile]);

  useEffect(() => {
    const handleSelectedChange = async () => {
      const storedId = sessionStorage.getItem("selectedCustomerId");
      if (!storedId) return;

      if (profile.type !== "admin") {
        const found = profile.customers.find(
          (c) => c.id.toString() === storedId
        );
        if (found) setActiveCustomer(found);
      } else {
        try {
          const res = await axios.get(`/api/v2/customers/${storedId}/`, {
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
          });
          setActiveCustomer(res.data);
        } catch (err) {
          console.error("Error fetching admin customer:", err);
        }
      }
    };

    window.addEventListener("selectedCustomerId-changed", handleSelectedChange);
    return () => {
      window.removeEventListener(
        "selectedCustomerId-changed",
        handleSelectedChange
      );
    };
  }, [profile]);

  const customer_type = activeCustomer?.customer_type;
  const customer = activeCustomer;
  const customer_id = activeCustomer?.id;
  const normalizeToEOM = (date) => {
    if (!date) return fallbackDate;
    return endOfMonth(new Date(date));
  };

  // Default date (never changes)
  const defaultDate = customer?.dashboard_date
    ? normalizeToEOM(parseISO(customer.dashboard_date))
    : fallbackDate;

  // Initial dash date
  const [initialDashDate, setInitialDashDate] = useState(() =>
    normalizeToEOM(getCustomerDashboardDate(customer))
  );

  const [dashDate, setDashDate] = useState(initialDashDate);

  const [dateCategory, setDateCategory] = useState(() =>
    format(initialDashDate, "yyyy-MM")
  );

  useEffect(() => {
    if (!activeCustomer) return;

    const stored = localStorage.getItem("dashboardDate");
    const newDate = stored
      ? normalizeToEOM(new Date(stored.split("-").map(Number)))
      : normalizeToEOM(getCustomerDashboardDate(activeCustomer));

    setDashDate(newDate);
    setInitialDashDate(newDate);
    setDateCategory(format(newDate, "yyyy-MM"));
  }, [activeCustomer]);

  // Listen for dashboardDate-changed events
  useEffect(() => {
    const handleDashboardDateChange = () => {
      if (!activeCustomer) return;

      const stored = localStorage.getItem("dashboardDate");
      const newDate = stored
        ? normalizeToEOM(new Date(stored.split("-").map(Number)))
        : normalizeToEOM(getCustomerDashboardDate(activeCustomer));

      setDashDate(newDate);
      setInitialDashDate(newDate);
      setDateCategory(format(newDate, "yyyy-MM"));
    };

    window.addEventListener("dashboardDate-changed", handleDashboardDateChange);
    return () =>
      window.removeEventListener(
        "dashboardDate-changed",
        handleDashboardDateChange
      );
  }, [activeCustomer]);

  const updateDate = (newDate) => {
    const normalized = normalizeToEOM(newDate);
    setDashDate(normalized);
    setDateCategory(format(normalized, "yyyy-MM"));
  };

  const location = useLocation();
  const navigate = useNavigate();

  const tabFromUrl = location.pathname.includes("/detail")
    ? "detail"
    : "metric";
  const [tab, setTab] = useState(tabFromUrl);

  useEffect(() => {
    if (profile?.type === "client") {
      const urlTab = location.pathname.includes("/detail")
        ? "detail"
        : "metric";
      if (tab !== urlTab) {
        setTab(urlTab);
      }
    }
  }, [location.pathname, tab, profile?.type]);

  const [adjustedView, setAdjustedView] = useState(() => {
    const stored = sessionStorage.getItem("adjustedView");
    return stored ? JSON.parse(stored) : false;
  });

  const handleToggleAdjusted = () => {
    setAdjustedView((prev) => {
      const next = !prev;
      sessionStorage.setItem("adjustedView", JSON.stringify(next));

      window.dispatchEvent(
        new CustomEvent("adjustedView-changed", { detail: next })
      );

      return next;
    });
  };

  useEffect(() => {
    const handler = (e) => {
      setAdjustedView(e.detail);
    };

    window.addEventListener("adjustedView-changed", handler);

    return () => window.removeEventListener("adjustedView-changed", handler);
  }, []);

  return (
    <ClientDashboardLayout>
      <ChakraProvider theme={theme}>
        <Stack bg="#F4F4F2" minH={"100vh"}>
          {isMobile && (
            <Card
              position="fixed"
              zIndex={999}
              borderRadius={0}
              h="72px"
              borderBottom="1px solid #e0e0e0"
              width="100%"
            >
              <HStack
                justifyContent="space-between"
                alignItems="center"
                h={"100%"}
                w="100%"
                px={4}
              >
                <Button
                  bg="white"
                  _hover={{ color: "white" }}
                  _active={{ color: "white" }}
                  w="50px"
                  h="50px"
                  p={2}
                  onClick={() => {
                    window.dispatchEvent(new Event("sidebar:expand"));
                  }}
                >
                  <Menu
                    style={{ width: "50px", height: "100%" }}
                    color="black"
                  />
                </Button>

                <HStack>
                  <DashboardDropdown
                    key={dashDate?.toISOString()}
                    height="36px"
                    setEnd={updateDate}
                    dateFromNav={defaultDate}
                    endDate={dashDate}
                    category={dateCategory}
                    setCategory={setDateCategory}
                    monthAmount={customer_type === "trial" ? 3 : 12}
                    alterButtonSize={true}
                    includeAllFromMin={customer_type === "trial" ? false : true}
                  />

                  {activeCustomer?.is_demo ===
                    false &&
                    (
                  <Box ml={"10px"}>
                    <TargetTypeToggle></TargetTypeToggle>
                  </Box> )}

                  {(profile?.type === "admin" || customer_type === "trial") && (
                    <IconButton
                      ml="10px"
                      aria-label="Toggle Adjusted View"
                      icon={
                        adjustedView ? <Check size={18} /> : <Eye size={18} />
                      }
                      onClick={handleToggleAdjusted}
                      variant={adjustedView ? "solid" : "outline"}
                      size="sm"
                      height="36px"
                      width={"36px"}
                    />
                  )}
                </HStack>
              </HStack>
            </Card>
          )}
          <Card
            borderRadius={0}
            h={isMobile ? "50px" : "72px"}
            borderBottom="1px solid #e0e0e0"
            mt={isMobile ? "72px" : "0px"}
            zIndex={300}
            position={"fixed"}
          >
            <Tabs index={tab === "metric" ? 0 : 1}>
              <Grid
                templateColumns="1fr 2fr 1fr 275fr"
                width="100%"
                alignItems="center"
                p="0px 20px"
                h={isMobile ? "50px" : "72px"}
              >
                <TabList
                  display="flex"
                  alignItems="center"
                  h={isMobile ? "50px" : "74px"}
                >
                  <Tab
                    _focus={{
                      outline: "none",
                      border: "none",
                      boxShadow: "none",
                    }}
                    _selected={{
                      borderBottom: "3px solid #536DEC",
                      color: "#536DEC",
                      fontWeight: 700,
                    }}
                    cursor="pointer"
                    onClick={() => {
                      setTab("metric");

                      navigate("/performance-review/metric");
                    }}
                    _active={{ background: "none" }}
                    width={isMobile ? "80px" : "90px"}
                    h="100%"
                    p={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize={isMobile ? "14px" : "16px"} margin="0">
                      Metric View
                    </Text>
                  </Tab>
                  <Tab
                    _focus={{
                      outline: "none",
                      border: "none",
                      boxShadow: "none",
                    }}
                    _selected={{
                      borderBottom: "3px solid #536DEC",
                      color: "#536DEC",
                      fontWeight: 700,
                    }}
                    cursor="pointer"
                    onClick={() => {
                      setTab("detail");

                      navigate("/performance-review/detail");
                    }}
                    _active={{ background: "none" }}
                    width={isMobile ? "85px" : "105px"}
                    h="100%"
                    p={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    whiteSpace="nowrap"
                  >
                    <Text fontSize={isMobile ? "14px" : "16px"} margin="0">
                      Detail View
                    </Text>
                  </Tab>
                </TabList>

                <HStack
                  gridColumn={9}
                  justify="flex-end"
                  align="center"
                  spacing="10px"
                >
                  {!isMobile && (
                    <DashboardDropdown
                      key={dashDate?.toISOString()}
                      height="36px"
                      dateFromNav={defaultDate}
                      setEnd={updateDate}
                      endDate={dashDate}
                      category={dateCategory}
                      setCategory={setDateCategory}
                      monthAmount={customer_type === "trial" ? 3 : 12}
                      includeAllFromMin={customer_type === "trial" ? false : true}
                    />
                  )}

                  {activeCustomer?.is_demo ===
                    false &&
                    (
                  <Box>
                    <TargetTypeToggle></TargetTypeToggle>
                  </Box>
                    )}
                  {(profile?.type === "admin" || customer_type === "trial") &&
                    !isMobile && (
                      <IconButton
                        aria-label="Toggle Adjusted View"
                        icon={
                          adjustedView ? <Check size={18} /> : <Eye size={18} />
                        }
                        onClick={handleToggleAdjusted}
                        variant={adjustedView ? "solid" : "outline"}
                        size="sm"
                        height="36px"
                        width="36px"
                      />
                    )}
                </HStack>
              </Grid>
            </Tabs>
          </Card>
          {tab === "metric" && (
            <MetricView customer_id={customer_id} dashDate={dashDate} />
          )}
          {tab === "detail" && (
            <DetailView
              customer_id={customer_id}
              dashDate={dashDate}
              customer_type={customer_type}
            />
          )}
        </Stack>
      </ChakraProvider>
    </ClientDashboardLayout>
  );
};

export default PerformanceReview;
